/**
 * `MissionDetailDialog` — the magnifier-glass view for one mission row
 * in the Lexiun Agents pane.
 *
 * Opens via `useUIStore.openMissionDetail(missionId)`; the SPA mounts
 * exactly one of these at the App-shell level (alongside its harness
 * twin) and the dialog renders whichever id the store currently holds.
 *
 * Reads from `useMissionsStore` first (the polled summary + any
 * already-fetched record) so the dialog opens instantly with whatever
 * the pane already had. On open, this component fires `getMission(id)`
 * in the background and `upsertMission`s the full record so the
 * ledger-enriched fields (per-node timings/cost, totals, recentEvents)
 * fill in. The polling cadence on the parent pane also keeps the
 * record fresh while the dialog is open.
 *
 * Plan: ai-docs/lexide-right-sidebar-implementation-plan.md §K-bis.
 * Backend contract: ai-docs/handoffs/team-agents-detail-dialog-backend-handoff-2026-06-22.md
 */
import { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Pause,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { DetailDialogShell } from "./DetailDialogShell";
import { useMissionsPoll } from "@/hooks/useMissionsPoll";
import { useMissionsStore } from "@/stores/useMissionsStore";
import { useHarnessIndex } from "@/hooks/useHarnessIndex";
import { useUIStore } from "@/stores/useUIStore";
import { AwaitingQuestionsList } from "./AwaitingQuestionsList";
import { normalizeProgressPct } from "@/lib/agents/progress-pct";
import {
  getMission,
  missionNodes,
  TERMINAL_MISSION_STATES,
  type MissionEvent,
  type MissionRecord,
  type MissionState,
  type NodeStatus,
} from "@/api/lx-mission";

export function MissionDetailDialog() {
  const { t } = useTranslation();
  const missionId = useUIStore((s) => s.openMissionDetailId);
  const close = useUIStore((s) => s.closeDetailDialog);
  const slot = useMissionsStore((s) =>
    missionId ? s.missions.get(missionId) : undefined,
  );
  const upsertMission = useMissionsStore((s) => s.upsertMission);
  const harnessIndex = useHarnessIndex();
  const { cancelMission } = useMissionsPoll();
  const [fetchError, setFetchError] = useState<string | null>(null);

  // On open, fetch the full record. Re-runs if the user closes + opens
  // a different mission. The pane's polling keeps it fresh thereafter.
  useEffect(() => {
    if (!missionId) {
      setFetchError(null);
      return;
    }
    let canceled = false;
    getMission(missionId)
      .then((rec) => {
        if (canceled) return;
        upsertMission(rec);
        setFetchError(null);
      })
      .catch((err) => {
        if (canceled) return;
        setFetchError(err instanceof Error ? err.message : "fetch failed");
      });
    return () => {
      canceled = true;
    };
  }, [missionId, upsertMission]);

  if (!missionId || !slot) return null;

  const record: MissionRecord | undefined = slot.record;
  const goal = record?.goal ?? slot.summary.goal;
  const state = record?.state ?? slot.summary.state;
  const isTerminal = (TERMINAL_MISSION_STATES as readonly string[]).includes(
    state,
  );
  const showCancel = !isTerminal && !slot.isCanceling;

  async function handleCancel() {
    if (!missionId) return;
    const confirmed = window.confirm(
      t("rightSidebar.confirmCancel", { goal }),
    );
    if (!confirmed) return;
    try {
      await cancelMission(missionId);
      close();
    } catch {
      /* poll error surfaces via store; dialog stays open so the user
         can see the failure context */
    }
  }

  return (
    <DetailDialogShell
      open={true}
      onClose={close}
      titleId="mission-detail-dialog-title"
      testid="mission-detail-dialog"
      header={
        <MissionHeader
          titleId="mission-detail-dialog-title"
          goal={goal}
          state={state}
          openedAt={record?.openedAt ?? slot.summary.openedAt}
          startedAt={record?.startedAt}
          completedAt={record?.completedAt ?? slot.summary.completedAt}
          totals={record?.totals}
        />
      }
      footer={
        <>
          <button
            type="button"
            data-testid="mission-detail-copy-id"
            onClick={() => navigator.clipboard?.writeText(missionId)}
            className="rounded px-3 py-1.5 text-xs text-slate-600 hover:bg-cream-100 dark:text-slate-300 dark:hover:bg-coffee-800"
          >
            {t("detailDialog.copyId")}
          </button>
          {showCancel ? (
            <button
              type="button"
              data-testid="mission-detail-cancel"
              onClick={handleCancel}
              className="rounded bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-900/40"
            >
              {t("detailDialog.cancelMission")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={close}
            data-testid="mission-detail-close-action"
            className="rounded bg-cream-200/60 px-3 py-1.5 text-xs font-medium text-coffee-700 hover:bg-cream-300/60 dark:bg-coffee-800 dark:text-slate-200 dark:hover:bg-coffee-700"
          >
            {t("detailDialog.close")}
          </button>
        </>
      }
    >
      {fetchError && !record ? (
        <p
          data-testid="mission-detail-fetch-error"
          className="mb-5 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-300"
        >
          {t("detailDialog.fetchError", { error: fetchError })}
        </p>
      ) : null}

      {record?.error ? <MissionErrorBlock error={record.error} /> : null}

      {record?.awaitingUser ? (
        <MissionAwaitingUserBlock awaitingUser={record.awaitingUser} />
      ) : null}

      <MissionNodesList record={record} harnessIndex={harnessIndex} />

      <MissionRecentEvents events={record?.recentEvents} />
    </DetailDialogShell>
  );
}

// ─── Subcomponents (kept in-file because they're pure presentation
//     glue for this one dialog) ────────────────────────────────────

function MissionHeader(props: {
  titleId: string;
  goal: string;
  state: MissionState;
  openedAt: number;
  startedAt?: number;
  completedAt?: number;
  totals?: { costUsd: number; tokensIn: number; tokensOut: number };
}) {
  const { t } = useTranslation();
  const elapsedMs =
    props.startedAt && props.completedAt
      ? props.completedAt - props.startedAt
      : props.startedAt && !props.completedAt
        ? Date.now() - props.startedAt
        : undefined;
  return (
    <>
      <div className="mb-2 flex items-center gap-2">
        <MissionStateBadge state={props.state} />
        <span className="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400 dark:text-slate-500">
          {t("detailDialog.missionLabel")}
        </span>
      </div>
      <h2
        id={props.titleId}
        className="text-lg font-medium leading-snug text-coffee-900 dark:text-cream-100"
      >
        {props.goal}
      </h2>
      {/* Metric chips — only render the ones that actually carry data,
          and avoid duplicating the same timestamps in two places. The
          body's TIMELINE section is the canonical "what happened when"
          surface; this row is the at-a-glance scoreboard. */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
        {elapsedMs != null ? (
          <HeaderChip
            testid="mission-detail-header-elapsed"
            label={t("detailDialog.elapsed")}
            value={formatDurationMs(elapsedMs)}
          />
        ) : null}
        {props.totals ? (
          <HeaderChip
            testid="mission-detail-header-totals"
            label={t("detailDialog.cost")}
            value={`$${props.totals.costUsd.toFixed(4)} · ${props.totals.tokensIn}↑ ${props.totals.tokensOut}↓`}
          />
        ) : null}
        {props.startedAt ? (
          <HeaderChip
            label={t("detailDialog.startedAt")}
            value={formatAbsoluteWithSeconds(props.startedAt)}
            tooltip={formatAbsoluteWithSecondsLong(props.startedAt)}
          />
        ) : (
          <HeaderChip
            label={t("detailDialog.openedAt")}
            value={formatAbsoluteWithSeconds(props.openedAt)}
            tooltip={formatAbsoluteWithSecondsLong(props.openedAt)}
          />
        )}
        {props.completedAt ? (
          <HeaderChip
            label={t("detailDialog.completedAt")}
            value={formatAbsoluteWithSeconds(props.completedAt)}
            tooltip={formatAbsoluteWithSecondsLong(props.completedAt)}
          />
        ) : null}
      </div>
    </>
  );
}

function HeaderChip(props: {
  label: string;
  value: string;
  tooltip?: string;
  testid?: string;
}) {
  return (
    <span
      data-testid={props.testid}
      title={props.tooltip ?? `${props.label}: ${props.value}`}
      className="inline-flex items-baseline gap-1 rounded-full bg-cream-100/70 px-2.5 py-1 tabular-nums text-coffee-700 dark:bg-coffee-800/40 dark:text-slate-300"
    >
      <span className="text-[9px] font-medium uppercase tracking-[0.08em] text-slate-400 dark:text-slate-500">
        {props.label}
      </span>
      <span>{props.value}</span>
    </span>
  );
}

function MissionStateBadge({ state }: { state: MissionState }) {
  const { t } = useTranslation();
  const cls = "h-3 w-3";
  let icon: React.ReactNode;
  switch (state) {
    case "queued":
      icon = <Clock className={`${cls} text-slate-500`} />;
      break;
    case "submitted":
    case "planning":
    case "running":
    case "synthesizing":
      icon = <Loader2 className={`${cls} animate-spin text-blue-500`} />;
      break;
    case "awaiting_user":
      icon = <Pause className={`${cls} text-amber-500`} />;
      break;
    case "completed":
      icon = <CheckCircle2 className={`${cls} text-emerald-500`} />;
      break;
    case "failed":
      icon = <XCircle className={`${cls} text-red-500`} />;
      break;
    case "canceled":
      icon = <XCircle className={`${cls} text-slate-400`} />;
      break;
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-cream-200/40 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-coffee-700 dark:bg-coffee-800/60 dark:text-slate-200">
      {icon}
      {t(`rightSidebar.missionState.${state}`)}
    </span>
  );
}

function MissionNodesList({
  record,
  harnessIndex,
}: {
  record?: MissionRecord;
  harnessIndex: Map<string, { tool: { label: string; description: string } }>;
}) {
  const { t } = useTranslation();
  if (!record) {
    return (
      <section className="mb-6">
        <SectionHeading label={t("detailDialog.nodes")} />
        <p
          data-testid="mission-detail-nodes-loading"
          className="text-xs text-slate-500 dark:text-slate-400"
        >
          {t("detailDialog.loading")}
        </p>
      </section>
    );
  }
  // Absent `nodes` falls into the existing empty branch below rather than
  // throwing — "this run has no steps yet" is exactly what a queued mission
  // means, and the empty copy already says it.
  const nodes = Object.values(missionNodes(record));
  if (nodes.length === 0) {
    return (
      <section className="mb-6">
        <SectionHeading label={t("detailDialog.nodes")} />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t("detailDialog.nodesEmpty")}
        </p>
      </section>
    );
  }
  return (
    <section className="mb-6">
      <SectionHeading label={t("detailDialog.nodes")} count={nodes.length} />
      <ol
        data-testid="mission-detail-nodes-list"
        className="space-y-2"
      >
        {nodes.map((node, idx) => {
          // Heading precedence per the 2026-06-23 relay:
          //   1. node.summary           — lawyer-facing step label
          //      (mission-service forwards DagNode.summary onto the
          //      node state at dispatch — e.g. "Extract parties from
          //      the petition PDF"). RENDER THIS when present.
          //   2. manifest.tool.label    — the harness's friendly name
          //      (e.g. "PDF intake"). Available for any harness with
          //      a registered manifest, regardless of ledger version.
          //   3. node.harnessId         — raw slug fallback ("intake-pdf").
          const manifest = harnessIndex.get(node.harnessId);
          const stepSummary = node.summary;
          const harnessLabel = manifest?.tool.label ?? node.harnessId;
          // ⚠ manifest `tool.description` is DELIBERATELY NOT rendered here.
          // It is the LLM TOOL-CALLING description (harn-sdk manifest
          // schema.ts:52-56 — it sits in the `tool` block beside
          // `permission_group`/`concierge_visible`), written for a MODEL
          // deciding whether to invoke the harness. It is English, carries
          // backticked identifiers, and advises which tool to call
          // ("Prefer launching the m-write-piece mission…"). Showing it to a
          // lawyer inside his own case is a category error, not a polish gap
          // (tester-galvao finding 20260913T053839Z, operator-raised).
          // The lawyer-facing text is `node.summary`, with `tool.label` as
          // the fallback — both already drive the heading above.
          const heading = stepSummary ?? harnessLabel;
          // Render "PDF intake" as a small uppercase chip below the
          // heading WHEN the heading itself came from the step-summary
          // (otherwise the heading already IS the harness label — no
          // need to duplicate).
          const showAgentChip =
            !!stepSummary && harnessLabel !== stepSummary;
          return (
            <li
              key={node.nodeId}
              data-testid={`mission-detail-node-${node.nodeId}`}
              data-status={node.status}
              className={cn(
                "flex items-stretch gap-3 overflow-hidden rounded-lg border bg-cream-50/40 dark:bg-coffee-950/30",
                "border-cream-200/60 dark:border-coffee-800/60",
              )}
            >
              {/* Left accent rail — colored by status. Doubles as the
                  step index marker for the lawyer to count steps. */}
              <div
                className={cn(
                  "flex w-9 shrink-0 flex-col items-center justify-start gap-1 py-3 text-[10px] font-medium tabular-nums",
                  nodeAccentBg(node.status),
                )}
              >
                <NodeStatusGlyph status={node.status} />
                <span className="text-slate-500 dark:text-slate-400">
                  {idx + 1}
                </span>
              </div>
              <div className="flex min-w-0 flex-1 items-start gap-3 py-3 pr-3 text-xs">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-coffee-900 dark:text-cream-100">
                    {heading}
                  </div>
                  {showAgentChip ? (
                    <div className="mt-0.5 text-[10px] uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
                      {harnessLabel}
                    </div>
                  ) : null}
                  {node.progress ? (
                    <div className="mt-1.5 truncate rounded bg-cream-100/70 px-2 py-0.5 text-[10px] tabular-nums text-coffee-700 dark:bg-coffee-800/40 dark:text-slate-300">
                      {node.progress.step}
                      {normalizeProgressPct(node.progress.pct) !== null
                        ? ` · ${normalizeProgressPct(node.progress.pct)}%`
                        : ""}
                      {node.progress.message
                        ? ` — ${node.progress.message}`
                        : ""}
                    </div>
                  ) : null}
                  {node.error ? (
                    <div
                      data-testid={`mission-detail-node-${node.nodeId}-error`}
                      className="mt-1.5 flex items-center gap-1.5 rounded border border-red-200/60 bg-red-50/60 px-2 py-1 text-[11px] text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300"
                    >
                      <span>
                        {t(`detailDialog.errorCodes.${node.error.code}`, {
                          defaultValue: t("detailDialog.errorCodes._default"),
                        })}
                      </span>
                      <code
                        className="rounded bg-red-100/70 px-1 py-0 font-mono text-[9px] uppercase tracking-wide text-red-700/70 dark:bg-red-900/40 dark:text-red-300/70"
                        title={t("detailDialog.errorCodeTooltip")}
                      >
                        {node.error.code}
                      </code>
                    </div>
                  ) : null}
                </div>
                {/* Right-side metrics — only render when the values
                    actually exist. Stacked, tabular, quiet. */}
                <div className="shrink-0 text-right text-[10px] tabular-nums text-slate-500 dark:text-slate-400">
                  {node.durationMs != null ? (
                    <div className="text-coffee-700 dark:text-slate-300">
                      {formatDurationMs(node.durationMs)}
                    </div>
                  ) : node.startedAt && !node.completedAt ? (
                    <div className="text-blue-600 dark:text-blue-300">
                      {t("detailDialog.running")}
                    </div>
                  ) : null}
                  {node.costUsd != null ? (
                    <div>${node.costUsd.toFixed(4)}</div>
                  ) : null}
                  {node.startedAt ? (
                    <div
                      className="mt-0.5 text-slate-400 dark:text-slate-500"
                      title={formatAbsoluteWithSecondsLong(node.startedAt)}
                    >
                      {formatAbsoluteWithSeconds(node.startedAt)}
                    </div>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** Subtle status-derived background tint for the left accent rail of a
 *  step card. Mirrors the glyph color so the eye can sweep the column
 *  and triage at a glance (green = done, red = failed, etc.). */
function nodeAccentBg(status: NodeStatus): string {
  switch (status) {
    case "completed":
      return "bg-emerald-100/50 dark:bg-emerald-950/30";
    case "failed":
      return "bg-red-100/50 dark:bg-red-950/30";
    case "dispatched":
      return "bg-blue-100/50 dark:bg-blue-950/30";
    case "awaiting_user":
      return "bg-amber-100/50 dark:bg-amber-950/30";
    case "pending":
    default:
      return "bg-cream-200/40 dark:bg-coffee-800/40";
  }
}

function MissionRecentEvents({ events }: { events?: MissionEvent[] }) {
  const { t } = useTranslation();
  if (!events || events.length === 0) {
    return (
      <section className="mb-1">
        <SectionHeading label={t("detailDialog.recentEvents")} />
        <p
          data-testid="mission-detail-events-empty"
          className="text-xs italic text-slate-500 dark:text-slate-400"
        >
          {t("detailDialog.recentEventsEmpty")}
        </p>
      </section>
    );
  }
  // recentEvents is FIFO oldest→newest per the contract; show newest first.
  const ordered = [...events].reverse();
  return (
    <section className="mb-1">
      <SectionHeading
        label={t("detailDialog.recentEvents")}
        count={events.length}
      />
      {/* Vertical-line timeline with status dots — narrative scan,
          not log dump. The line lives on the dot column; events stay
          right-aligned so the eye sweeps the time gutter. */}
      <ol
        data-testid="mission-detail-events-list"
        className="relative ml-1.5 border-l border-cream-200/60 dark:border-coffee-800/60"
      >
        {ordered.map((ev, i) => (
          <li
            key={`${ev.at}-${i}`}
            className="relative flex items-start gap-3 py-1.5 pl-4 text-xs"
          >
            <span
              aria-hidden
              className={cn(
                "absolute -left-[5px] top-2.5 h-2.5 w-2.5 rounded-full ring-2 ring-cream-50 dark:ring-coffee-900",
                eventDotColor(ev),
              )}
            />
            <span
              className="w-16 shrink-0 tabular-nums text-slate-500 dark:text-slate-400"
              title={formatAbsoluteWithSecondsLong(ev.at)}
            >
              {formatAbsoluteWithSeconds(ev.at)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="font-medium text-coffee-700 dark:text-slate-200">
                {t(`detailDialog.eventType.${eventLabelKey(ev)}`, ev.type)}
              </span>
              {ev.nodeId ? (
                <span className="ml-1.5 inline-block rounded bg-cream-100/70 px-1.5 py-0.5 text-[10px] tabular-nums text-coffee-700 dark:bg-coffee-800/40 dark:text-slate-300">
                  {ev.nodeId}
                </span>
              ) : null}
              {ev.detail ? (
                <span className="ml-1.5 text-slate-500 dark:text-slate-400">
                  — {ev.detail}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** Dot color for the vertical-timeline rail — mirrors the event-type
 *  semantics so a lawyer can scan the column and triage at a glance. */
function eventDotColor(ev: MissionEvent): string {
  switch (ev.type) {
    case "node_completed":
      return "bg-emerald-500";
    case "node_failed":
    case "mission_canceled":
    // A wake that exhausted its retries UNDELIVERED is a failure, not chatter.
    // It used to fall through to the neutral slate dot, so a lost wake looked
    // exactly like routine traffic.
    case "wake_failed":
      return "bg-red-500";
    case "node_awaiting_user":
      return "bg-amber-500";
    case "node_awaiting_handback":
      // ⚖ F6: the discriminator for "does a PERSON owe this reply?" is the
      // COUNTERPARTY, never the park kind. A concierge-addressed handback park
      // is owed BY THE LAWYER (the concierge is a human's proxy), so it gets
      // the same amber as an explicit ask. An agent-addressed one — or one
      // whose counterparty we cannot read — must not borrow that urgency.
      return personOwedHandback(ev) ? "bg-amber-500" : "bg-slate-400";
    case "node_dispatched":
      return "bg-blue-500";
    case "state_changed":
    case "wake_sent":
    default:
      return "bg-slate-400";
  }
}

/** The bus alias that stands for a HUMAN. `mission-coordinator.ts:1368` pushes
 *  `outcome.to` as the event detail, so the counterparty rides on the event
 *  already — no wire change was needed to tell these apart.
 *  ⚠ If another human-proxy alias is ever introduced, it belongs HERE; a park
 *  addressed to a person that this predicate does not recognise degrades to the
 *  UNKNOWN form, which is the safe direction (it never claims the lawyer is
 *  free), but it also never tells him he is being waited on. */
const HUMAN_PROXY_ALIASES = new Set(["concierge"]);

/** `true` = a person owes the reply · `false` = an agent does · `null` = the
 *  counterparty is not on the event, so we do not know and must not guess. */
export function personOwedHandback(ev: MissionEvent): boolean | null {
  const to = ev.detail?.trim().toLowerCase();
  if (!to) return null;
  return HUMAN_PROXY_ALIASES.has(to);
}

/** Which `eventType.*` key to render. Only the handback park branches: the
 *  SAME event means "answer me" or "an agent is waiting" depending on the
 *  counterparty, and rendering one flat label made it read as the opposite of
 *  the truth on a lawyer-owed park (`lexiun-agent-bus-plan`, 2026-09-13:
 *  "inverted, and inverted in the reassuring direction"). */
export function eventLabelKey(ev: MissionEvent): string {
  if (ev.type !== "node_awaiting_handback") return ev.type;
  const owed = personOwedHandback(ev);
  if (owed === null) return "node_awaiting_handback";
  return owed ? "node_awaiting_handback_person" : "node_awaiting_handback_agent";
}

function MissionErrorBlock({
  error,
}: {
  error: { code: string; message: string };
}) {
  const { t } = useTranslation();
  // Same sanitization principle as the harness dialog (operator
  // 2026-06-23) — never expose the raw `error.message` (often
  // platform-internal). Render a friendly localized label keyed by
  // the error code; show the code as a small chip for support.
  const friendly = t(`detailDialog.errorCodes.${error.code}`, {
    defaultValue: t("detailDialog.errorCodes._default"),
  });
  return (
    <section
      data-testid="mission-detail-error"
      className="mb-4 rounded-lg border border-red-200/60 bg-red-50/60 p-3 text-xs dark:border-red-900/40 dark:bg-red-950/30"
    >
      <div className="flex items-start gap-2 font-medium text-red-700 dark:text-red-300">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>{friendly}</span>
      </div>
      <code
        data-testid="mission-detail-error-code"
        className="mt-1 inline-block rounded bg-red-100/70 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-red-700/80 dark:bg-red-900/40 dark:text-red-300/80"
        title={t("detailDialog.errorCodeTooltip")}
      >
        {error.code}
      </code>
    </section>
  );
}

/** ⚠ `questions` was deliberately absent from this prop until 2026-08-17 —
 *  the block announced the park without ever showing what was asked, so a
 *  lawyer could see "waiting on you" and never learn the question outside
 *  chat. It renders READ-ONLY (see `AwaitingQuestionsList`); answering
 *  stays chat-mediated by ruling. */
function MissionAwaitingUserBlock({
  awaitingUser,
}: {
  awaitingUser: { nodeId: string; expiresAt: number; questions?: unknown[] };
}) {
  const { t } = useTranslation();
  return (
    <section
      data-testid="mission-detail-awaiting-user"
      className="mb-4 rounded border border-amber-300/60 bg-amber-50 p-3 text-xs dark:border-amber-700/60 dark:bg-amber-950/30"
    >
      <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-300">
        <Pause size={14} />
        {t("detailDialog.awaitingUserLabel")}
      </div>
      <p className="mt-1 text-amber-800/90 dark:text-amber-300/80">
        {t("detailDialog.awaitingUserOnNode", { nodeId: awaitingUser.nodeId })}
      </p>
      <AwaitingQuestionsList questions={awaitingUser.questions} />
      <p className="mt-1 text-amber-800/70 dark:text-amber-300/70">
        {t("detailDialog.awaitingUserExpires", {
          when: new Date(awaitingUser.expiresAt).toLocaleString(),
        })}
      </p>
    </section>
  );
}

// ─── Shared bits ───────────────────────────────────────────────────────

function NodeStatusGlyph({ status }: { status: NodeStatus }) {
  const cls = "mt-0.5 h-3 w-3 shrink-0";
  switch (status) {
    case "pending":
      return <Clock className={`${cls} text-slate-400`} aria-hidden />;
    case "dispatched":
      return <Loader2 className={`${cls} animate-spin text-blue-500`} aria-hidden />;
    case "completed":
      return <CheckCircle2 className={`${cls} text-emerald-500`} aria-hidden />;
    case "failed":
      return <XCircle className={`${cls} text-red-500`} aria-hidden />;
    case "awaiting_user":
      return <Pause className={`${cls} text-amber-500`} aria-hidden />;
    default:
      return null;
  }
}

function SectionHeading({
  label,
  count,
}: {
  label: string;
  count?: number;
}) {
  return (
    <h3 className="mb-2 flex items-baseline gap-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 dark:text-slate-400">
      {label}
      {typeof count === "number" ? (
        <span className="inline-flex items-center rounded-full bg-cream-200/60 px-1.5 py-0 text-[10px] font-medium tabular-nums tracking-normal text-coffee-700 dark:bg-coffee-800/60 dark:text-slate-300">
          {count}
        </span>
      ) : null}
    </h3>
  );
}


function TimingChip({ label, value }: { label: string; value?: number }) {
  if (!value) return null;
  return (
    <span
      className="tabular-nums"
      title={formatAbsoluteWithSecondsLong(value)}
    >
      {label}: {formatAbsoluteWithSeconds(value)}
    </span>
  );
}

// ─── Format helpers — kept local because the dialog is the only
//     consumer; if they spread further they should move to a util.
// Forensic display: SECONDS matter (operator 2026-06-23). The dialog
// is a magnifier-glass view, so wall-clock precision wins over the
// chat-style "X minutes ago" relative form. ───

function formatDurationMs(ms: number): string {
  if (ms < 1_000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)} s`;
  if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)} m`;
  return `${(ms / 3_600_000).toFixed(1)} h`;
}

function formatAbsoluteWithSeconds(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hms = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  if (sameDay) return hms;
  const sameYear = d.getFullYear() === now.getFullYear();
  const dm = d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "2-digit",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  return `${dm} ${hms}`;
}

function formatAbsoluteWithSecondsLong(ts: number): string {
  return new Date(ts).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}
